import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Wallet, Send, CreditCard, TrendingUp, Shield, Zap, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';

const LandingPage = () => {
  return (
    <div className="min-h-screen bg-[#030014] relative overflow-hidden">
      {/* Background Effects */}
      <div className="absolute inset-0 dark-mesh" />
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[800px] bg-[#7000FF] opacity-20 blur-[150px] rounded-full" />
      
      {/* Hero Section */}
      <div className="relative z-10">
        {/* Header */}
        <header className="px-6 md:px-12 py-6 flex justify-between items-center">
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-full gradient-primary flex items-center justify-center">
              <Wallet className="w-6 h-6 text-white" />
            </div>
            <span className="text-2xl font-primary font-bold text-white">CRYPTOPAY</span>
          </div>
          <Link to="/auth">
            <Button className="bg-transparent border border-[#7000FF] text-[#7000FF] hover:bg-[#7000FF] hover:text-white rounded-full px-6" data-testid="header-signin-btn">
              Sign In
            </Button>
          </Link>
        </header>

        {/* Hero Content */}
        <div className="px-6 md:px-12 py-20 max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h1 className="text-5xl md:text-7xl font-primary font-bold text-white mb-6 tracking-tight uppercase leading-tight">
              Your Crypto.
              <br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#7000FF] to-[#00F0FF]">
                Your Currency.
              </span>
            </h1>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto mb-8 font-secondary">
              Seamlessly convert crypto to AED. Spend anywhere with Silver or Gold payment cards. The future of finance in UAE.
            </p>
            <Link to="/auth">
              <Button className="bg-[#7000FF] hover:bg-[#5B00CC] text-white rounded-full px-10 py-6 text-lg font-bold neon-glow-primary hover:shadow-[0_0_30px_rgba(112,0,255,0.8)] transition-all duration-300 transform hover:-translate-y-1" data-testid="hero-get-started-btn">
                Get Started <ArrowRight className="ml-2 inline" />
              </Button>
            </Link>
          </motion.div>

          {/* Features Grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-20">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="glass-card p-8 rounded-2xl hover:border-[#7000FF]/50 transition-all duration-300"
            >
              <div className="w-14 h-14 rounded-full bg-[#7000FF]/20 flex items-center justify-center mb-4">
                <Wallet className="w-7 h-7 text-[#7000FF]" />
              </div>
              <h3 className="text-xl font-primary font-bold text-white mb-3">Multi-Crypto Wallet</h3>
              <p className="text-gray-400 font-secondary">Support for BTC, ETH, USDT, USDC, BNB, and SOL. Manage all your assets in one place.</p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              className="glass-card p-8 rounded-2xl hover:border-[#00F0FF]/50 transition-all duration-300"
            >
              <div className="w-14 h-14 rounded-full bg-[#00F0FF]/20 flex items-center justify-center mb-4">
                <TrendingUp className="w-7 h-7 text-[#00F0FF]" />
              </div>
              <h3 className="text-xl font-primary font-bold text-white mb-3">Instant Conversion</h3>
              <p className="text-gray-400 font-secondary">Convert crypto to AED instantly at competitive rates. No hidden fees.</p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.4 }}
              className="glass-card p-8 rounded-2xl hover:border-[#FF0080]/50 transition-all duration-300"
            >
              <div className="w-14 h-14 rounded-full bg-[#FF0080]/20 flex items-center justify-center mb-4">
                <CreditCard className="w-7 h-7 text-[#FF0080]" />
              </div>
              <h3 className="text-xl font-primary font-bold text-white mb-3">Premium Cards</h3>
              <p className="text-gray-400 font-secondary">Choose Silver or Gold cards with exclusive benefits and global acceptance.</p>
            </motion.div>
          </div>

          {/* Additional Features */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.5 }}
              className="glass-card p-8 rounded-2xl hover:border-[#00FF94]/50 transition-all duration-300"
            >
              <div className="w-14 h-14 rounded-full bg-[#00FF94]/20 flex items-center justify-center mb-4">
                <Send className="w-7 h-7 text-[#00FF94]" />
              </div>
              <h3 className="text-xl font-primary font-bold text-white mb-3">Easy Transfers</h3>
              <p className="text-gray-400 font-secondary">Send crypto to any wallet via Ethereum, BSC, Solana, or TRON networks.</p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.6 }}
              className="glass-card p-8 rounded-2xl hover:border-[#FFD700]/50 transition-all duration-300"
            >
              <div className="w-14 h-14 rounded-full bg-[#FFD700]/20 flex items-center justify-center mb-4">
                <Zap className="w-7 h-7 text-[#FFD700]" />
              </div>
              <h3 className="text-xl font-primary font-bold text-white mb-3">Bank Integration</h3>
              <p className="text-gray-400 font-secondary">Transfer AED directly to your UAE bank account seamlessly.</p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.7 }}
              className="glass-card p-8 rounded-2xl hover:border-[#C0C0C0]/50 transition-all duration-300"
            >
              <div className="w-14 h-14 rounded-full bg-[#C0C0C0]/20 flex items-center justify-center mb-4">
                <Shield className="w-7 h-7 text-[#C0C0C0]" />
              </div>
              <h3 className="text-xl font-primary font-bold text-white mb-3">Secure & Compliant</h3>
              <p className="text-gray-400 font-secondary">Bank-grade security with full UAE regulatory compliance.</p>
            </motion.div>
          </div>
        </div>

        {/* CTA Section */}
        <div className="px-6 md:px-12 py-20 text-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5, delay: 0.8 }}
            className="max-w-4xl mx-auto glass-card p-12 rounded-3xl"
          >
            <h2 className="text-4xl md:text-5xl font-primary font-bold text-white mb-6">READY TO START?</h2>
            <p className="text-xl text-gray-400 mb-8 font-secondary">Join thousands managing their crypto finances with CryptoPay</p>
            <Link to="/auth">
              <Button className="bg-[#00F0FF] hover:bg-[#00C2CC] text-black rounded-full px-12 py-6 text-lg font-bold neon-glow-secondary hover:shadow-[0_0_40px_rgba(0,240,255,0.7)] transition-all duration-300 transform hover:-translate-y-1" data-testid="cta-create-account-btn">
                Create Your Account
              </Button>
            </Link>
          </motion.div>
        </div>

        {/* Footer */}
        <footer className="px-6 md:px-12 py-8 border-t border-white/10 text-center">
          <p className="text-gray-500 font-secondary text-sm">&copy; 2026 CryptoPay. Powered by Coinbase Commerce. All rights reserved.</p>
        </footer>
      </div>
    </div>
  );
};

export default LandingPage;
